import { useState } from "react";
import api from "@/lib/api";
import { initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Trash, UserPlus, DotsThreeVertical, PaperPlaneRight,
  ShieldStar, ArrowClockwise, Clock,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ROLE_INFO, PermissionsMatrix } from "./PermissionsMatrix";

export function TeamPanel({ users, setUsers, currentUser, isAdmin, ssoEnabled }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "", role: "agent" });
  const [inviting, setInviting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const reload = () => api.get("/users").then((r) => setUsers(r.data));

  const sendInvite = async () => {
    if (!invite.email || !invite.name) return toast.error("Email and name required");
    setInviting(true);
    try {
      await api.post("/users/invite", invite);
      toast.success(`Invitation sent to ${invite.email}`);
      setInviteOpen(false);
      setInvite({ email: "", name: "", role: "agent" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to invite");
    } finally { setInviting(false); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`${u.name} is now ${ROLE_INFO[role]?.label || role}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  /**
   * Toggle a single team membership on/off for a user. Admin-only.
   * Empty `teams` means "any" — the user shows up in every assignee
   * picker, same as before this field existed. We send the full
   * de-duplicated list rather than a diff so the server contract stays
   * simple and the UI state can't drift mid-update.
   */
  const toggleTeam = async (u, team) => {
    const current = Array.isArray(u.teams) ? u.teams : [];
    const next = current.includes(team)
      ? current.filter((t) => t !== team)
      : [...current, team];
    try {
      await api.patch(`/users/${u.id}`, { teams: next });
      toast.success(
        next.length
          ? `${u.name}: ${next.map((t) => t === "it" ? "IT" : "Systems").join(" + ")}`
          : `${u.name}: any team`,
      );
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(`${u.name} ${u.is_active ? "deactivated" : "reactivated"}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const resend = async (u) => {
    try {
      await api.post(`/users/${u.id}/resend-invite`);
      toast.success(`Invitation resent to ${u.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="hairline rounded-lg p-5 bg-white" data-testid="settings-team">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading font-semibold">Team ({users.length})</div>
          <p className="text-xs text-slate-500 mt-1">
            {ssoEnabled
              ? "Users are managed via your Microsoft Entra ID security group. Add or remove someone in Entra, then they sign in with Microsoft."
              : "Invite agents and admins. Invitees receive an email with a link to set their password."}
          </p>
        </div>
        {isAdmin && !ssoEnabled && (
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button data-testid="team-invite-btn" className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1">
                <UserPlus size={14} weight="bold" /> Invite user
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Invite a teammate</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-700">Full name</Label>
                  <Input data-testid="invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="mt-1 bg-[#FFFFFF] border-slate-200" placeholder="Jane Smith" />
                </div>
                <div>
                  <Label className="text-xs text-slate-700">Work email</Label>
                  <Input data-testid="invite-email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="mt-1 bg-[#FFFFFF] border-slate-200" placeholder="jane@auroraenergy.co" />
                </div>
                <div>
                  <Label className="text-xs text-slate-700">Role</Label>
                  <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                    <SelectTrigger data-testid="invite-role" className="mt-1 bg-[#FFFFFF] border-slate-200 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="agent">Agent — handles tickets</SelectItem>
                      <SelectItem value="admin">Admin — full settings access</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-[11px] text-slate-500 font-mono bg-slate-50 p-2 rounded">
                  Requesters don&apos;t need an account — they get a private link in their auto-ack email. Only invite teammates here.
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="invite-submit" onClick={sendInvite} disabled={inviting} className="bg-[#4f4c90] hover:bg-[#5F5CA5]">
                  {inviting ? "Sending…" : "Send invite"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mt-4 divide-y divide-slate-100">
        {users.map((u) => {
          const isSelf = u.id === currentUser?.id;
          const pending = u.pending_invite;
          const inactive = u.is_active === false;
          const info = ROLE_INFO[u.role] || ROLE_INFO.agent;
          return (
            <div key={u.id} className="flex items-center gap-3 py-2.5" data-testid={`user-row-${u.email}`}>
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs">
                {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full rounded-full object-cover" /> : initials(u.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm flex items-center gap-1.5 flex-wrap">
                  {u.name}
                  {isSelf && <span className="text-[10px] text-slate-500 font-mono">(you)</span>}
                  {pending && <span className="chip !text-[#F59E0B] !border-[#F59E0B]/30 bg-[#F59E0B]/10 gap-1"><Clock size={9} weight="fill" /> Pending</span>}
                  {inactive && !pending && <span className="chip !text-red-400 !border-red-500/30 bg-red-500/10">Deactivated</span>}
                </div>
                <div className="text-[11px] text-slate-500 font-mono">{u.email}</div>
                {Array.isArray(u.teams) && u.teams.length > 0 && (
                  <div className="flex items-center gap-1 mt-0.5">
                    {u.teams.includes("it") && (
                      <span className="chip !text-[#39A36E] !border-[#39A36E]/40 bg-[#39A36E]/10 !text-[10px] !py-0.5">IT</span>
                    )}
                    {u.teams.includes("systems") && (
                      <span className="chip !text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10 !text-[10px] !py-0.5">Systems</span>
                    )}
                  </div>
                )}
              </div>
              <span className={`chip capitalize ${info.color}`}>{info.label}</span>

              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1 border-slate-200 bg-slate-100 hover:bg-slate-200 text-xs"
                      data-testid={`user-menu-${u.email}`}
                    >
                      Manage <DotsThreeVertical size={12} weight="bold" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white border-slate-200 min-w-52">
                    {["admin", "agent", "requester"].filter(r => r !== u.role).map(r => (
                      <DropdownMenuItem key={r} onClick={() => changeRole(u, r)} className="text-xs cursor-pointer">
                        <ShieldStar size={12} className="mr-2" /> Change role to {ROLE_INFO[r].label}
                      </DropdownMenuItem>
                    ))}
                    {u.role !== "requester" && (
                      <>
                        <DropdownMenuSeparator className="bg-slate-200" />
                        <DropdownMenuItem
                          onClick={() => toggleTeam(u, "it")}
                          className="text-xs cursor-pointer"
                          data-testid={`user-toggle-team-it-${u.email}`}
                        >
                          <ShieldStar size={12} className="mr-2" />
                          {Array.isArray(u.teams) && u.teams.includes("it") ? "Remove from IT" : "Add to IT"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => toggleTeam(u, "systems")}
                          className="text-xs cursor-pointer"
                          data-testid={`user-toggle-team-systems-${u.email}`}
                        >
                          <ShieldStar size={12} className="mr-2" />
                          {Array.isArray(u.teams) && u.teams.includes("systems") ? "Remove from Systems" : "Add to Systems"}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-slate-200" />
                    {pending && !ssoEnabled && (
                      <DropdownMenuItem onClick={() => resend(u)} className="text-xs cursor-pointer">
                        <PaperPlaneRight size={12} className="mr-2" /> Resend invitation
                      </DropdownMenuItem>
                    )}
                    {!isSelf && (
                      <DropdownMenuItem onClick={() => toggleActive(u)} className="text-xs cursor-pointer">
                        <ArrowClockwise size={12} className="mr-2" /> {inactive ? "Reactivate" : "Deactivate"}
                      </DropdownMenuItem>
                    )}
                    {!isSelf && (
                      <DropdownMenuItem
                        onClick={(e) => { e.preventDefault(); setDeleteTarget(u); }}
                        className="text-xs cursor-pointer text-red-400 focus:text-red-400"
                        data-testid={`user-delete-${u.email}`}
                      >
                        <Trash size={12} className="mr-2" /> Remove from team
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })}
      </div>

      <PermissionsMatrix />

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-400 flex items-center gap-2">
              <Trash size={18} weight="fill" /> Remove {deleteTarget?.name}?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>
              <span className="font-mono text-slate-900">{deleteTarget?.email}</span> will lose access immediately.
              Their tickets and comments stay — just their account and any active assignments are removed.
            </p>
            <p className="text-[11px] text-slate-500">
              Prefer to keep them for the audit trail? Choose <span className="font-mono">Deactivate</span> instead —
              same effect (blocked from login) but the record stays.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="border-slate-200 bg-slate-100 h-9"
              onClick={() => setDeleteTarget(null)}
              data-testid="user-delete-cancel"
            >Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 h-9 gap-1"
              data-testid="user-delete-confirm"
            >
              <Trash size={14} /> {deleting ? "Removing…" : "Remove user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
